import { client } from '@api';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { DiscountType, ViaxOrder } from '@types';
import {
  SET_PROMO_CODE_TO_CART_MUTATION, SetPromoCodesToCartVariables, SetPromoCodesToCartResponse,
  GET_CART_DISCOUNTS_QUERY, GetCartDiscountsResponse,
} from '@requests/cart';
import { hasPhysicalProducts } from '@utils';

interface AddPromotionToCartProps {
  cartId: string;
  prmId: string;
}

type AddPromotionToCartResult = { wSetPromoCodes: ViaxOrder } & GetCartDiscountsResponse;

export const addPromotionToCart = createAsyncThunk<AddPromotionToCartResult, AddPromotionToCartProps>(
  'cart/addPromotionToCart',
  async ({ cartId, prmId }, { rejectWithValue }) => {
    try {
      const variables: SetPromoCodesToCartVariables = {
        orderUid: cartId,
        promoCodes: [prmId],
      };
      const updatedCart = await client.request<SetPromoCodesToCartResponse>(SET_PROMO_CODE_TO_CART_MUTATION, variables);

      if (updatedCart.wSetPromoCodes.result.status !== 'OK') {
        return rejectWithValue(prmId);
      }

      const cartDiscounts = await client.request<GetCartDiscountsResponse>(GET_CART_DISCOUNTS_QUERY, { uid: cartId });

      const cartData = updatedCart.wSetPromoCodes.result.data;
      const appliedCoupon = cartData?.corPromoCodes?.[0];
      const couponApplied = !!appliedCoupon?.prmId;

      const { discounts } = cartDiscounts.wGetCartDiscounts;
      const couponDiscount = discounts.couponTotalDiscount;

      const orderItemDiscount = discounts.orderItemDiscounts?.[0];

      const isFreeTrial = orderItemDiscount?.discountType === DiscountType.FREETRIAL;
      const cartHasPhysicalProducts = hasPhysicalProducts(cartData?.hiConsistsOf);

      const isFreeShipping = appliedCoupon?.wFreeStandardShipping && cartHasPhysicalProducts;

      if (couponApplied && !couponDiscount && !isFreeTrial && !isFreeShipping) {
        const removeVariables = { orderUid: cartId, promoCodes: [] };
        await client.request(SET_PROMO_CODE_TO_CART_MUTATION, removeVariables);
        return rejectWithValue(prmId);
      }

      return { wSetPromoCodes: updatedCart.wSetPromoCodes.result.data, ...cartDiscounts };
    } catch (error) {
      return rejectWithValue(prmId);
    }
  },
);
